
import {
  computed, defineComponent, ref,
} from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useMainStore } from '@/store/mainStore';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useAlertsStore } from '@/modules/alerts/store/alertsStore';
import {
  BOTTOM_POPUP_TYPES, MEDIA_TYPES, NAVIGATION_PAGES, PHOTO_VIEW_TYPE, SHARE_ACCESS, SHARE_TYPES,
} from '@/types/enums';
import MixedMediaSettingsPopUp from '@/components/BottomPopUp/PopUpTypes/Settings/MixedMediaSettingsPopUp.vue';
import { ICase, ICaseFilesManage, IMedia } from '@/types/interfaces';
import DriveService from '@/modules/drive/services/DriveService';
import MoveToPopUp from '@/components/BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';
import SharePopUp from '@/components/BottomPopUp/PopUpTypes/Share/SharePopUp.vue';
import AppNetworkConfirmPopUp from '@/components/AppNetworkConfirmPopUp.vue';

export default defineComponent({
  props: {
    file: { type: Object as () => IMedia, required: true },
  },
  components: {
    SharePopUp,
    AppQuestionPopUp,
    MoveToPopUp,
    MixedMediaSettingsPopUp,
    AppNetworkConfirmPopUp,
  },
  emits: ['closePopUp'],
  setup(props, { emit }) {
    const router = useRouter();
    const route = useRoute();

    const mainStore = useMainStore();
    const driveStore = useDriveStore();
    const alertsStore = useAlertsStore();

    const currentMedia = ref(0);
    const caseToUnshare = ref({} as ICase);
    const confirmUnsharing = ref(false);
    const isQuestionPopUp = ref(false);
    const showSkeleton = ref(false);
    const isSharePopUp = ref(false);
    const isMediaSettingsPopUp = ref(true);
    const isMoveToPopUp = ref(false);
    const isMixedView = ref(false);
    const currentItem = computed(() => props.file);
    const currentItemType = computed(() => (currentItem.value?.type === MEDIA_TYPES.PHOTO ? MEDIA_TYPES.PHOTO : MEDIA_TYPES.FILES));

    const isNetwork = computed(() => route.meta.isNetwork || false);
    const navigationPage = computed(() => route.meta.navigationPage);
    const isCase = computed(
      () => navigationPage.value === NAVIGATION_PAGES.DRIVE_CASES
        || navigationPage.value === NAVIGATION_PAGES.NETWORK_CASES
        || navigationPage.value === NAVIGATION_PAGES.CASE_EDIT
        || navigationPage.value === NAVIGATION_PAGES.CASE_VIEW,
    );

    const mediaList = computed(() => {
      if (route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_PHOTOS || route.meta.navigationPage === NAVIGATION_PAGES.DRIVE || route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_FILES) {
        return isMixedView.value ? driveStore.mixedData.data.filter((el) => el.type === 'FILE').map((el) => el.file) : driveStore.photos.data;
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.NETWORK || route.meta.navigationPage === NAVIGATION_PAGES.NETWORK_PHOTOS || route.meta.navigationPage === NAVIGATION_PAGES.NETWORK_CASES) {
        if (isCase.value && driveStore.currentCase.id) {
          return [...driveStore.photoList, ...driveStore.currentCase.files] || [];
        }
        return isMixedView.value
          ? driveStore.networkMixedData.data.filter((el) => el.type === 'FILE').map((el) => el.file)
          : driveStore.networkPhotos.data;
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.DRIVE_FOLDER) {
        return driveStore.currentFolder?.mixedData?.filter((el) => el.type === 'FILE').map((el) => el.file) || [];
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.ALERTS) {
        return alertsStore.notifications.data.filter((el) => el.type === 'SHARE_FILE' && el.sharedInfo !== null).map((el) => el.sharedInfo);
      }
      if (route.meta.navigationPage === NAVIGATION_PAGES.CASE_VIEW || route.meta.navigationPage === NAVIGATION_PAGES.CASE_EDIT) {
        return [...driveStore.currentCase.photos, ...driveStore.currentCase.files];
      }
      const isMessagePage = (route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_ID || route.meta.navigationPage === NAVIGATION_PAGES.MESSAGES_GROUP_CHAT_ID);
      if (isMessagePage && driveStore.currentCase.id) {
        return [...driveStore.currentCase.photos, ...driveStore.currentCase.files];
      }
      return [currentItem.value];
    });

    function openPopUp(type: BOTTOM_POPUP_TYPES) {
      if (isCase.value && isSharePopUp.value) {
        return;
      }
      mainStore.BottomPopUpType = type;
    }

    function openMovePopUp() {
      isMediaSettingsPopUp.value = false;
      isMoveToPopUp.value = true;
    }
    function openQuestionPopUp() {
      isMediaSettingsPopUp.value = false;
      isQuestionPopUp.value = true;
    }
    function saveMediaAsCopy() {
      showSkeleton.value = true;
      isMediaSettingsPopUp.value = false;
      driveStore.saveMediaAsCopy(currentItem.value!.id).then((response) => {
        if (response.success) {
          emit('closePopUp');
          if (isNetwork.value) {
            router.push('/drive');
          }
        }
      });
    }
    function updateLocation(folderId: string | null) {
      currentItem.value!.folderId = folderId;
    }
    function removeMediaFromSharing() {
      const id = currentItem.value?.id as unknown as string;
      const networkCases = [...driveStore.networkCases?.data];

      let existInCase = false;
      // const caseToUnshare = {} as ICase;

      for (let i = 0; i < networkCases.length; i += 1) {
        const photoExist = (networkCases[i] as ICase).photos.filter((el: IMedia) => el.id === id);
        const filesExist = (networkCases[i] as ICase).files.filter((el: IMedia) => el.id === id);
        if (photoExist.length || filesExist.length) {
          existInCase = true;
          caseToUnshare.value = networkCases[i];
          break;
        }
      }
      // Remove below for remove Items from Case inside Shared with me
      if (existInCase) {
        confirmUnsharing.value = true;
        return null;
      }
      if (id) {
        const selectedMedia = {
          ids: [id as string],
          accessType: SHARE_ACCESS.SPECIFIC_PEOPLE,
          target: '',
        };
        DriveService.removeFromSharing(selectedMedia).then(() => {
          driveStore.fetchNetworkPhotos(true).then(() => {
            driveStore.appPhotoViewMedia = undefined;
            driveStore.networkMixedData.data = driveStore.networkMixedData.data.filter((selectedMediaItem) => selectedMediaItem.id !== id);
          });
        });
      }
      return null;
    }
    async function unshareCase() {
      const caseId = caseToUnshare.value.id as string;

      if (caseId) {
        const selectedCase = {
          ids: [caseId as string],
          accessType: SHARE_ACCESS.SPECIFIC_PEOPLE,
          target: '',
        };

        const unshareIdx = driveStore.networkCases?.data.findIndex((el) => el.id === caseToUnshare.value.id);

        // eslint-disable-next-line no-unused-expressions
        driveStore.networkCases?.data.splice(unshareIdx, 1);

        try {
          driveStore.networkPhotos.page = 0;
          driveStore.networkPhotos.lastPage = -1;
          driveStore.networkMixedData.page = 0;
          driveStore.networkMixedData.lastPage = -1;
          await DriveService.removeFromSharing(selectedCase);
          await driveStore.fetchNetworkCases();
          await driveStore.fetchNetworkPhotos();
          await driveStore.fetchNetworkMixedData(false);
          driveStore.appPhotoViewMedia = undefined;
        } catch (error) {
          console.log(error);
        }
      }
    }
    function deletePhoto() {
      if (isCase.value) {
        const removeFilePayload = {
          id: driveStore.currentCase.id!,
          fileIds: [currentItem.value!.id],
        } as ICaseFilesManage;

        let idx = driveStore.photoList.findIndex((photo) => photo.id === currentItem.value?.id);
        driveStore.removeFilesFromCase(removeFilePayload);

        if (driveStore.photoList.length >= 1) {
          idx = (idx + 1) % driveStore.photoList.length;
          driveStore.appPhotoViewMedia = driveStore.photoList[idx];
          isMediaSettingsPopUp.value = false;
          isQuestionPopUp.value = false;
        } else {
          driveStore.appPhotoViewMedia = undefined;
        }
      } else {
        isQuestionPopUp.value = false;
        driveStore.deleteMedia(currentItem.value!.id, MEDIA_TYPES.PHOTO);
        driveStore.appPhotoViewMedia = undefined;
      }
    }

    function onShare() {
      isSharePopUp.value = true;
      isMediaSettingsPopUp.value = false;
    }

    function onSharePopupHide() {
      isSharePopUp.value = false;
      emit('closePopUp');
    }

    return {
      mainStore,
      driveStore,
      currentItem,
      currentItemType,
      isMediaSettingsPopUp,
      openMovePopUp,
      openQuestionPopUp,
      saveMediaAsCopy,
      openPopUp,
      removeMediaFromSharing,
      BOTTOM_POPUP_TYPES,
      mediaList,
      currentMedia,
      isMoveToPopUp,
      isSharePopUp,
      isQuestionPopUp,
      deletePhoto,
      SHARE_TYPES,
      updateLocation,
      confirmUnsharing,
      unshareCase,
      caseToUnshare,
      onShare,
      onSharePopupHide,
    };
  },
});
